/**
 * AuthContext - Firebase Authentication Context Provider
 *
 * Provides authentication state and methods throughout the app.
 * Uses Firebase SDK with onAuthStateChanged for real-time auth status.
 */

import { createContext, useContext, useEffect, useState, useMemo, ReactNode, useCallback } from 'react';
import type { User, UserCredential } from 'firebase/auth';
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  onAuthStateChanged,
  updateProfile,
} from 'firebase/auth';
import { auth } from '../lib/firebase/config';
import { registerUser, getCurrentUser } from '../services/api/auth';
import { entitlementService } from '../services/entitlement';

interface DBUserProfile {
  id: string;
  email: string;
  name: string;
  image?: string | null;
  emailVerified: Date | null;
  timezone?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  dbUserId: string | null;
  dbProfile: DBUserProfile | null;
  refreshProfile: () => Promise<void>;
  refreshUserVerificationStatus: () => Promise<void>;
  signInWithGoogle: () => Promise<UserCredential>;
  signInWithEmail: (email: string, password: string) => Promise<UserCredential>;
  signUpWithEmail: (email: string, password: string, displayName?: string) => Promise<UserCredential>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [dbUserId, setDbUserId] = useState<string | null>(null);
  const [dbProfile, setDbProfile] = useState<DBUserProfile | null>(null);

  /**
   * Refresh user profile from database
   */
  const refreshProfile = useCallback(async () => {
    if (!user) {
      setDbProfile(null);
      return;
    }

    try {
      const idToken = await user.getIdToken();
      const response = await getCurrentUser(idToken);
      if (response.user) {
        setDbProfile(response.user);
        console.log('✅ Profile refreshed from DB:', response.user);
      }
    } catch (error) {
      console.error('Failed to refresh profile:', error);
    }
  }, [user]);

  /**
   * Refresh user's email verification status from Firebase
   * Called after email verification to ensure the latest status is reflected
   */
  const refreshUserVerificationStatus = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return;
    }

    try {
      console.log('🔄 Refreshing email verification status...');
      await currentUser.reload();
      console.log('✅ User verification status refreshed:', { emailVerified: currentUser.emailVerified });
      // Force state update by triggering setUser
      setUser(currentUser);
    } catch (error) {
      console.error('Failed to refresh verification status:', error);
    }
  }, []);

  /**
   * Ensure user exists in database, auto-registering if needed
   */
  const ensureUserInDatabase = useCallback(async (firebaseUser: User) => {
    try {
      const idToken = await firebaseUser.getIdToken();

      // Check if user exists in database
      let currentUser = await getCurrentUser(idToken);

      // Handle beta access denial
      if (currentUser.betaAccessDenied) {
        const { code, message } = currentUser.betaAccessDenied;
        console.warn('🚫 Beta access denied during auth check:', code, message);

        // Store denial info in sessionStorage so LoginPage can display it
        sessionStorage.setItem('beta_access_denial', JSON.stringify({ code, message }));

        // Force sign out from Firebase
        await firebaseSignOut(auth);
        console.log('🔐 Signed out user due to beta access denial');

        // Clear local state
        setUser(null);
        setDbUserId(null);
        setDbProfile(null);

        return; // Exit early - user will be redirected by BetaGate or LoginPage
      }

      if (currentUser.needsRegistration) {
        // User authenticated in Firebase but not in database - auto-register
        console.log('Auto-registering new user in database:', firebaseUser.email);

        // Extract Google profile image URL from provider data
        const googleProfile = firebaseUser.providerData.find(
          provider => provider.providerId === 'google.com'
        );
        const googlePhotoURL = googleProfile?.photoURL || firebaseUser.photoURL;

        const registeredUser = await registerUser(idToken, {
          email: firebaseUser.email || '',
          name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
          firebaseUid: firebaseUser.uid,
          image: googlePhotoURL || undefined,
        });

        setDbUserId(registeredUser.id);
        console.log('User registered successfully with image:', googlePhotoURL || 'none');

        // Retry lookup after registration to confirm user exists
        // This handles race conditions where DB insert hasn't fully committed
        try {
          await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms
          const confirmedUser = await getCurrentUser(idToken);
          if (confirmedUser.user) {
            console.log('✅ Confirmed user exists in database after registration');
            setDbUserId(confirmedUser.user.id); // Update with confirmed ID
          }
        } catch (retryError) {
          console.warn('⚠️ Retry lookup after registration failed:', retryError);
          // Don't throw - user is registered, lookup will work on next page load
        }
      } else if (currentUser.user) {
        // User already exists in database
        setDbUserId(currentUser.user.id);
        console.log('✅ User already exists in database:', currentUser.user.id);
      }
    } catch (error) {
      console.error('❌ Failed to ensure user in database:', error);
      // Don't block auth flow - user can still access app with Firebase auth
      // DB registration can be retried later
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        // Skip database operations if on verification page to prevent interference
        const isOnVerifyPage = window.location.pathname === '/verify-email';
        if (!isOnVerifyPage) {
          // User signed in - ensure they exist in database
          await ensureUserInDatabase(firebaseUser);
        }
      } else {
        // User signed out - clear database ID and profile
        setDbUserId(null);
        setDbProfile(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [ensureUserInDatabase]);

  // Fetch DB profile when user signs in
  useEffect(() => {
    if (user && !loading) {
      refreshProfile();
    }
  }, [user, loading, refreshProfile]);

  const signInWithGoogle = async (): Promise<UserCredential> => {
    const provider = new GoogleAuthProvider();
    provider.addScope('https://www.googleapis.com/auth/userinfo.profile');
    provider.addScope('https://www.googleapis.com/auth/userinfo.email');
    return await signInWithPopup(auth, provider);
  };

  const signInWithEmail = async (email: string, password: string): Promise<UserCredential> => {
    return await signInWithEmailAndPassword(auth, email, password);
  };

  const signUpWithEmail = async (
    email: string,
    password: string,
    displayName?: string
  ): Promise<UserCredential> => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName && result.user) {
      await updateProfile(result.user, { displayName });
    }

    // Send email verification with custom branded template
    if (result.user) {
      try {
        // Note: No continueUrl provided - user stays on verification page after success
        // This prevents Firebase from redirecting and losing the verification context
        await sendEmailVerification(result.user);
        console.log('✅ Verification email sent successfully');
      } catch (error: any) {
        console.error('⚠️ Failed to send verification email:', error?.code || error);
        // Don't throw - user can resend from banner later
        // This prevents signup from failing if email service has issues
      }
    }

    return result;
  };

  const resendVerificationEmail = async (): Promise<void> => {
    if (!user) {
      throw new Error('No user signed in');
    }

    if (user.emailVerified) {
      throw new Error('Email is already verified');
    }

    // Note: No continueUrl provided - user stays on verification page after success
    await sendEmailVerification(user);
  };

  const signOut = async (): Promise<void> => {
    // Clear entitlement cache on sign out
    entitlementService.clearCache();
    await firebaseSignOut(auth);
  };

  const resetPassword = async (email: string): Promise<void> => {
    await sendPasswordResetEmail(auth, email);
  };

  const value = useMemo(
    () => ({
      user,
      loading,
      dbUserId,
      dbProfile,
      refreshProfile,
      refreshUserVerificationStatus,
      signInWithGoogle,
      signInWithEmail,
      signUpWithEmail,
      signOut,
      resetPassword,
      resendVerificationEmail
    }),
    [user, loading, dbUserId, dbProfile, refreshProfile, refreshUserVerificationStatus]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
